import moment from 'moment';
import { ConfirmationModalBean } from "../../components/modal/ConfirmationModalBean";
import {
    accounts,
    banks,
    byCountySummary,
    currencyAccounts,
    byGroupSummary,
    count,
    transactionsDownloads,
    transactionsIndividualDownloads,
    intradaysDownloads,
    VALUE_SERIES,
    userReportfiles
} from '../arsweb/RoutingPaths';
import {transactionReportCols} from "../../components/report/transactionReportCols";
import {intradayTransactionReportCols} from "../../components/report/intradayTransactionReportCols";
import {valueBalanceReportCols} from "../../components/report/valueBalanceReportCols";
import {downloadSettingsValueKey} from "fides-angularjs-components/src/fts-administration/fts-exportDownload-settings/fts-download-settings";
import {delimiterOptions} from "fides-angularjs-components/src/fts-administration/fts-exportDownload-settings/fts-export-settings";
import { MT94XUserSettingsMapper } from "./txt/MT94XUserSettingsMapper";
import { DEFAULT_DATE_TIME_FORMAT } from 'fides-angularjs-components/src/fts-services/fts-user-service';

export const FILE_TYPE = {
    'CSV': { contentType: 'application/csv;base64', mapFileName: fileName => fileName + '.csv',
        userFunction: "postUserReport", value: 'CSV'},
    'TXT': { contentType: 'data:application/txt;base64', mapFileName: fileName => fileName + '.txt',
        userFunction: "postUserReport", value: 'TXT' },
    // N.B. ZIP, PDF files type must be downloaded with: config.responseType = 'arraybuffer'
    // e.g. HTTPService.useBinaryGetHandler or AccountService.getBinaryUserReport:
    'ZIP': { contentType: 'application/zip', mapFileName: fileName => fileName + '.zip', userFunction: "postBinaryUserReport",
        value: 'ZIP' },
    'PDF': { contentType: 'application/pdf', mapFileName: fileName => fileName + '.pdf', userFunction: "postBinaryUserReport",
        value: 'PDF' },
    'XLSX': { contentType: 'application/xlsx', mapFileName: fileName => fileName + '.xlsx', userFunction: "postBinaryUserReport",
        value: 'XLSX' },
    'XLS': { contentType: 'application/xlsx', mapFileName: fileName => fileName + '.xlsx', userFunction: "postBinaryUserReport",
        value: 'XLS' }
};

const GROUP_URL = [ banks, byCountySummary, currencyAccounts, byGroupSummary ];

/** @ngInject */
export class ExportService {
    constructor(ArsConfig, userService) {
        this.ArsConfig = ArsConfig;
        this.$translate = userService.fsMainDependencies.$translate;
        this.userService = userService;
        this.sessionPersistance = userService.fsMainDependencies.sessionPersistance;
        this.mT94XUserSettingsMapper = new MT94XUserSettingsMapper(this.sessionPersistance);
    }

    processByFileMime(element, fileOptionType) {
        const fileMime = fileOptionType.fileMime;
        if(fileMime === FILE_TYPE.TXT.value) {
            this.processMT94X(element, fileOptionType);
            return FILE_TYPE.TXT;
        } else if(fileMime === FILE_TYPE.PDF.value) {
            this.processPDF(element, fileOptionType);
            return FILE_TYPE.PDF;
        } else if(fileMime === FILE_TYPE.CSV.value ||
            fileMime === FILE_TYPE.XLS.value ||fileMime === FILE_TYPE.XLSX.value) {
            this.processCSV(element, fileOptionType);
            return fileMime === FILE_TYPE.CSV.value ? FILE_TYPE.CSV : FILE_TYPE.XLS;
        }
        return {};
    }

    processMT94X(element, fileOptionType) {
        this.reportBody = {type: fileOptionType.type,
            title: fileOptionType.type.toLowerCase() + '_' + moment(new Date()).format('DDMMYYYY_HHmm'),
            unRead: element.unRead, lastStatement: element.lastStatement};
        element.mt94XBody = this.mT94XUserSettingsMapper.getMT94XUserSettings(fileOptionType.type);
        Object.assign(element.mt94XBody, this.reportBody);
    }

    processPDF(element, fileOptionType) {
        const userSettings = this.sessionPersistance.get(downloadSettingsValueKey);
        this.reportBody = {title: this.$translate.instant(fileOptionType.prefix) +
                moment(new Date()).format('DDMMYYYY_HHmm'), translations: this.getTranslations()};
        element.pdfBody = this.getCommonBody(element.unRead, element.lastStatement, element.withBalance);
        element.pdfBody.emptyPages = userSettings != null ? userSettings.emptyPages === 'SETTINGS_DOWNLOAD_PDF_EMPTY_PAGES_OPTION_A' : true;
        Object.assign(element.pdfBody, this.reportBody);
    }

    processCSV(element, fileOptionType) {
        const userSettings = this.sessionPersistance.get(downloadSettingsValueKey);
        const selectedDelimiter = (userSettings && userSettings.delimiter) ? delimiterOptions.filter(
            delimiter => delimiter.option === userSettings.delimiter) : [delimiterOptions[0]];

        const cols = this.getReportCols(fileOptionType.url, element);
        const headers = cols.map(col => col.title ? col.title : this.$translate.instant(col.title_key));
        const fields = cols.map(col => col.subfield ? col.field + '_val' : col.field);

        this.reportBody = {delimiter: selectedDelimiter[0].charValue,
            title: this.$translate.instant(fileOptionType.prefix) + moment(new Date()).format('DDMMYYYY_HHmm'),
            headers: headers, fields: fields};
        element.csvBody = this.getCommonBody(element.unRead, element.lastStatement, element.withBalance);
        Object.assign(element.csvBody, this.reportBody);
    }

    getReportCols(url, element){
        if(url != VALUE_SERIES) {
            return this.getTransactionCols(url, element.withStatementData)
        }
        return new valueBalanceReportCols(element, this.$translate);
    }

    getTransactionCols(url, withStatementData) {
        if(url === transactionsDownloads) return new transactionReportCols(withStatementData);
        if(url === transactionsIndividualDownloads) return new transactionReportCols(withStatementData);
        if(url === intradaysDownloads) return new intradayTransactionReportCols(withStatementData);
    }

    getService(controller) {
        return controller.getUserReport ? controller : controller.backendService ? controller.backendService : controller.service;
    }

    isGroupUrl(url) {
        return url && GROUP_URL.indexOf(url) > -1 || !url;
    }

    getDateTimePattern(){
        const dateTimePattern = this.userService.fsMainDependencies.settings.dateTimeFormat;
        return _.isNil(dateTimePattern) ? DEFAULT_DATE_TIME_FORMAT : dateTimePattern;
    }

    checkTotalElements(service, restUrl) {
        const url = this.isGroupUrl(restUrl) ? accounts : restUrl;
        return service.getUserData(url + count).then(data => this.total = data);
    }

    exceedsMax(modal, limit, returnedRows) {
        const confBean = new ConfirmationModalBean(this.$translate.instant('REPORT_LIMIT', {
            rowsLimit: limit,
            rowsCounter: returnedRows
        }), "EXPORT", modal);

        modal.show({
            templateUrl: 'app/components/modal/confirmation-modal.html',
            controller: 'ConfirmationModalController',
            controllerAs: 'confirmationModalController',
            size: 'md',
            backdrop: 'static',
            keyboard: false,
            resolve: { confirmationModalBean: () => confBean }
        });
    }

    confirmAsynchDocGeneration(userFunction, controller, body, reportType) {
        const cancel = 'cancel';
        controller.modal.cancel = () => {
            this.resetScreen(controller);
            controller.modal.hide(cancel);
        };

        controller.confirmAsynch().then((result) => {
            if(_.isNil(result)) {
                const service = this.getService(controller);
                this.toAsynch(controller.element ? controller.element : body,
                    service ? service : userFunction);
                if(controller.element) { // userreport execution ACCOUNTS & Value series
                    service[userFunction](userReportfiles, controller.element);
                    setTimeout(() => controller.resetScreen(), 1000);
                } else {
                   this.download(controller, userFunction, body, reportType);
                }
            }
        });
    }

    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    download(controller, userFunction, body, reportType){
        //overwritten by childs
    }

    toAsynch(element, backendService) {
        // asynch calls set timeout to avoid logout
        backendService.service.config.timeout = 1000;
        if(element.mt94XBody || element.pdfBody || element.csvBody) {
            if(element.mt94XBody){
                element.mt94XBody.asynch = true;
            }
            if(element.pdfBody) {
                element.pdfBody.asynch = true;
            }
            if(element.csvBody){
                element.csvBody.asynch = true;
            }
        } else {
            element.asynch = true;
        }
    }

    downloadFile(data, fileName, fileType, controller, userFunction, body, reportType) {
        const type = fileType.value ? fileType.value : fileType.reportType.fileMime;
        // detect empty xlsx
        if((type === FILE_TYPE.XLS.value || type === FILE_TYPE.XLSX.value) &&
            data instanceof ArrayBuffer && new DataView(data).byteLength === 0) {
            data = '';
        }

        if(data && data.status === 413) {
            this.confirmAsynchDocGeneration(userFunction, controller, body, reportType);
            return;
        }

        if (data === '' || !data) {
            this.resetScreen(controller);
            if (data === '') {
                controller.infoPop('NO_DATA_FOUND');
            }
            return;
        }

        this.resetScreen(controller);

        if(data.status) { // 502 request too long || 406 too big
            controller.infoPop(this.$translate.instant('MESS_'+ data.status));
            return;
        }

        this.serveFile(data, fileType, fileName);
    }

    serveFile(data, fileType, fileName) {
        const blob = new Blob([data], {type: fileType.contentType});

        if (!window.navigator.msSaveBlob) { // If msSaveBlob() is not supported,
            const url = window.URL.createObjectURL(blob);

            const anchorElem = document.createElement("a");
            anchorElem.style = "display:none";
            anchorElem.href = url;
            anchorElem.download = fileType.mapFileName(fileName);

            document.body.appendChild(anchorElem);
            anchorElem.click();

            document.body.removeChild(anchorElem);

            window.URL.revokeObjectURL(url);
        } else {
            window.navigator.msSaveBlob(blob, fileType.mapFileName(fileName));
        }
    }

    resetScreen(controller) {
        controller.isExecuting = false;
        if(controller.resetScreen) {
            if(this.getService(controller).config) {
                delete this.getService(controller).config.timeout;
            }
            controller.resetScreen();
        }
    }

    // ARSWEB-440: List of other countries disappear
    resetFilter(controller, backendService) {
        delete backendService.service.config.timeout;
        if (controller.resetFilter) {
            controller.resetFilter();
        }
    }

    createReportFilter(service, params) {
        service.service.config.params.reportFilter = {
            dateRange: params.dateRange, unRead: params.unRead, lastStatement: params.lastStatement };
    }

    constructReportServiceCall(service, params, body) {
        this.createReportFilter(service, params);

        body.title = this.$translate.instant(params.reportType.prefix) + moment(new Date()).format('DDMMYYYY_HHmm');
    }

    getCommonBody(unRead, lastStatement, withBalance){
        const dateTimePattern = this.getDateTimePattern();

        return {
            datePattern: dateTimePattern.substring(0, dateTimePattern.indexOf(' ')),
            dateTimePattern: dateTimePattern,
            numberFormat: this.userService.fsMainDependencies.settings.backendLocaleFormat,
            unRead: unRead, lastStatement: lastStatement, withBalance: withBalance };
    }

    /**
     * PDF report labels translation. The key corresponds to the select key in the xsl file
     */
    getTranslations() {
        return {
            accountNameLabel: this.$translate.instant('ACCOUNT_NAME'),
            accountNumberLabel: this.$translate.instant('ACCOUNT_NUMBER'),
            bankLabel: this.$translate.instant('BANK'),
            currencyLabel: this.$translate.instant('CURRENCY'),
            consolidatedCurrencyLabel: this.$translate.instant('CONSOLIDATED_CURRENCY'),
            clientIDLabel: this.$translate.instant('CLIENT_ID'),
            userNameLabel: this.$translate.instant('USER'),
            statementLabel: this.$translate.instant('STATEMENT'),
            transactionLabel: this.$translate.instant('TRANSACTION'),
            accountsLabel: this.$translate.instant('ACCOUNTS'),
            intradayLabel: this.$translate.instant('INTRADAY'),
            endOfDayLabel: this.$translate.instant('END_OF_DAY'),
            openingBalanceLabel: this.$translate.instant('OPEN_BAL'),
            closingBalanceLabel: this.$translate.instant('CLOSE_BAL'),
            bookingDateLabel: this.$translate.instant('BOOK_DATE'),
            valueDateLabel: this.$translate.instant('VALUE_DATE'),
            bookingTextLabel: this.$translate.instant('BOOK_TEXT'),
            supplementaryTextLabel: this.$translate.instant('SUPPLEMENT_TEXT'),
            accountOwnerRefLabel: this.$translate.instant('ACC_OWN_REF'),
            debitAmountLabel: this.$translate.instant('DEBIT_AMOUNT'),
            creditAmountLabel: this.$translate.instant('CREDIT_AMOUNT'),
            exportGeneratedByLabel: this.$translate.instant('EXPORT_GENERATED_BY'),
            atLabel: this.$translate.instant('AT'),
            numberLabel: this.$translate.instant('NUMBER'),
            creditSumLabel: this.$translate.instant('CREDIT_SUM'),
            debitSumLabel: this.$translate.instant('DEBIT_SUM'),
            balanceLabel: this.$translate.instant('BALANCE'),
            balanceAccLabel: this.$translate.instant('BALANCE_ACC'),
            balanceConsLabel: this.$translate.instant('BALANCE_CONS'),
            noDataLabel: this.$translate.instant('NO_TRANSACTIONS'),
            numberOriginalCalculated: this.$translate.instant('NUMBER_ORIGINAL_CALCULATED'),
            reportLabel: this.$translate.instant('REPORT'),
            pageLabel: this.$translate.instant('PAGE'),
            lastClosingBalLabel: this.$translate.instant('LAST_CLOSING_BAL'),
            lastClosingDateLabel: this.$translate.instant('LAST_CLOSING_DATE'),
            totalLabel: this.$translate.instant('TOTAL'),
            valueSeriesLabel: this.$translate.instant('PAGE_TITLE.VALUE_SERIES'),
            valueSeriesSummaryLabel: this.$translate.instant('PAGE_TITLE.VALUE_SERIES_SUMMARY'),
            entriesAmountLabel: this.$translate.instant('ENTRIES_AMNT'),
            entriesAmountWithPreLabel: this.$translate.instant('PRENOTATION_AMNT'),
            valueAmountLabel: this.$translate.instant('VALUE_AMNT'),
            valueAmountWithPreLabel: this.$translate.instant('VALUE_PRENOTATION_AMNT'),
            countryLabel: this.$translate.instant('COUNTRY'),
            closingDateLabel: this.$translate.instant('CLOSE_DATE'),
            typeLabel: this.$translate.instant('TYPE')
        };
    }
}
